/**
 * @file BulletinAnnouncementPopup.tsx
 * @description 系統公告彈窗組件 — 在用戶登入後首次訪問時，自動顯示未讀公告。
 *
 * ## 行為說明
 * 1. 組件 mount 時檢查 sessionStorage 是否已顯示過彈窗（`SESSION_KEY`）
 * 2. 若已顯示過 → 不請求 API，提前退出（防重弹）
 * 3. 若未顯示過 → 立即寫入 sessionStorage flag，然後請求 GET /api/bulletin
 * 4. 過濾出：isRead === false && publishedAt != null && archivedAt == null
 * 5. 按 isPinned DESC, publishedAt DESC 排序，最多顯示 MAX_DISPLAY 條
 * 6. 無未讀公告 → 靜默不彈窗
 * 7. 有未讀公告 → 打開 Modal
 * 8. 每條公告可獨立「標記已讀」（樂觀更新）
 * 9. 「全部已讀」調用 POST /api/bulletin/read-all
 * 10. 「知道了」關閉彈窗
 * 11. 「查看全部公告」跳轉 /bulletin/
 *
 * ## 靜默失敗策略
 * 公告彈窗為增強功能，API 失敗時靜默收起，不顯示錯誤提示，不影響主業務。
 */

import { useCallback, useEffect, useState } from 'react';
import { Button, Modal, Tag, Tooltip } from 'antd';
import { message } from '@/lib/antd-app';
import { Bell, CheckCircle2, Info, Megaphone, Pin } from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useNavigate } from '@tanstack/react-router';
import { apiClient } from '@/lib/api-client';
import { color, font, radius, space, text, weight } from '@/styles/tokens';

/* 加載 relativeTime 插件（模塊級），確保 fromNow() 可用。
 * locale 由應用入口（main.tsx / App.tsx）統一設置，此處不重複設定。 */
dayjs.extend(relativeTime);

/* ════════════════════════════════════════════════
 * 常量
 * ════════════════════════════════════════════════ */

/** sessionStorage key，用於防止同一 session 重複彈出公告 */
const SESSION_KEY = 'bulletin-popup-shown';

/** 彈窗最多展示的公告數量 */
const MAX_DISPLAY = 5;

/* ════════════════════════════════════════════════
 * 類型聲明（本組件內部使用，不從其他文件 import）
 * ════════════════════════════════════════════════ */

/**
 * 後端 GET /api/bulletin 回傳的單條公告結構。
 * 包含 LEFT JOIN bulletin_reads 衍生的 isRead / readAt。
 */
interface Bulletin {
  id: number;
  title: string;
  body: string;
  audience: string;
  isPinned: boolean;
  publishedAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: number | null;
  isRead: boolean;
  readAt: string | null;
}

/**
 * 後端 GET /api/bulletin 回傳的列表結構
 */
interface BulletinListResponse {
  items: Bulletin[];
}

/* ════════════════════════════════════════════════
 * 工具函數
 * ════════════════════════════════════════════════ */

/**
 * 將 HTML 富文本轉換為純文本摘要，去除標籤後截取前 120 字符。
 * @param html - 含 HTML 標籤的公告 body
 * @returns 去標籤後的純文本摘要（最多 120 字）
 */
export function getPlainSummary(html: string): string {
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120);
}

/**
 * 格式化公告發布時間，超過 7 天顯示絕對日期，否則顯示相對時間。
 * @param dateStr - ISO 日期字符串
 * @returns 格式化後的時間字符串
 */
export function formatPublishTime(dateStr: string): string {
  const date = dayjs(dateStr);
  const diffDays = dayjs().diff(date, 'day');
  return diffDays > 7 ? date.format('YYYY-MM-DD') : date.fromNow();
}

/**
 * 過濾並排序未讀公告，用於決定彈窗展示哪些公告。
 * 過濾條件：isRead === false && publishedAt != null && archivedAt == null
 * 排序：isPinned DESC, publishedAt DESC
 * 上限：MAX_DISPLAY 條
 *
 * @param items - 原始公告列表（來自 GET /api/bulletin）
 * @param maxDisplay - 最大展示數量，默認 MAX_DISPLAY
 * @returns 過濾排序後的未讀公告列表
 */
export function filterAndSortUnread(items: Bulletin[], maxDisplay = MAX_DISPLAY): Bulletin[] {
  const unread = items.filter((b) => !b.isRead && b.publishedAt != null && b.archivedAt == null);

  unread.sort((a, b) => {
    if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
    const aTime = a.publishedAt ?? a.createdAt;
    const bTime = b.publishedAt ?? b.createdAt;
    return bTime < aTime ? -1 : bTime > aTime ? 1 : 0;
  });

  return unread.slice(0, maxDisplay);
}

/* ════════════════════════════════════════════════
 * 子組件：單條公告卡片
 * ════════════════════════════════════════════════ */

interface BulletinCardProps {
  /** 公告數據 */
  item: Bulletin;
  /** 是否已被用戶標記為已讀（樂觀更新後可能與 item.isRead 不同） */
  markedRead: boolean;
  /** 觸發單條標記已讀 */
  onMarkRead: (id: number) => void;
  /** 是否正在加載中（標記中）*/
  loading: boolean;
}

/**
 * 單條公告卡片組件，展示標題、摘要、時間，支持單條標記已讀。
 * 置頂公告左側橙色豎條 + Pin 圖標；普通公告展示 Info 圖標。
 * 已讀後卡片透明度降低（opacity: 0.5）。
 */
function BulletinCard({ item, markedRead, onMarkRead, loading }: BulletinCardProps) {
  const isPinned = item.isPinned;
  const summary = getPlainSummary(item.body);
  const publishTime = formatPublishTime(item.publishedAt ?? item.createdAt);
  const isEffectivelyRead = markedRead || item.isRead;

  return (
    <div
      style={{
        display: 'flex',
        gap: 0,
        borderRadius: radius.md,
        border: `1px solid ${isPinned ? color.state.warning + '40' : color.border.default}`,
        overflow: 'hidden',
        opacity: isEffectivelyRead ? 0.5 : 1,
        transition: 'opacity 200ms ease',
        background: isPinned ? color.state.warningSoft : color.surface.muted,
      }}
    >
      {/* 置頂公告左側橙色豎條 */}
      {isPinned && (
        <div
          style={{
            width: 3,
            background: color.state.warning,
            flexShrink: 0,
          }}
        />
      )}

      {/* 內容區域 */}
      <div
        style={{
          flex: 1,
          padding: `${space[3]}px ${space[4]}px`,
          minWidth: 0,
        }}
      >
        {/* 頂部 meta 行：圖標 + 標題 + 時間 + 標記按鈕 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: space[2],
          }}
        >
          {/* 公告類型圖標 */}
          <span
            style={{
              color: isPinned ? color.state.warning : color.state.info,
              flexShrink: 0,
              marginTop: 2,
            }}
          >
            {isPinned ? <Pin size={14} /> : <Info size={14} />}
          </span>

          {/* 標題 */}
          <span
            style={{
              flex: 1,
              fontFamily: font.display,
              fontSize: text.md,
              fontWeight: weight.semibold,
              color: color.text.primary,
              lineHeight: 1.4,
              wordBreak: 'break-word',
            }}
          >
            {item.title}
          </span>

          {/* 已讀標記按鈕 */}
          {!isEffectivelyRead && (
            <Tooltip title="標記已讀">
              <Button
                type="text"
                size="small"
                icon={<CheckCircle2 size={13} />}
                loading={loading}
                onClick={() => onMarkRead(item.id)}
                style={{
                  color: color.state.success,
                  flexShrink: 0,
                  padding: '0 4px',
                  height: 24,
                }}
              />
            </Tooltip>
          )}
        </div>

        {/* 摘要文本 */}
        {summary && (
          <p
            style={{
              margin: `${space[1]}px 0 0 ${space[2] + 14 + space[2]}px`,
              fontSize: text.sm,
              color: color.text.tertiary,
              lineHeight: 1.5,
              wordBreak: 'break-word',
            }}
          >
            {summary}
            {getPlainSummary(item.body).length >= 120 && '…'}
          </p>
        )}

        {/* 底部時間行 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: space[2],
            marginTop: space[2],
            marginLeft: space[2] + 14 + space[2],
          }}
        >
          {isPinned && (
            <Tag
              style={{
                fontSize: text.xs,
                padding: '0 6px',
                borderColor: color.state.warning + '60',
                color: color.state.warning,
                background: 'transparent',
              }}
            >
              置頂
            </Tag>
          )}
          <span
            style={{
              fontSize: text.xs,
              color: color.text.muted,
            }}
          >
            {publishTime}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
 * 主組件：BulletinAnnouncementPopup
 * ════════════════════════════════════════════════ */

/**
 * 系統公告彈窗組件。
 *
 * 應在應用 layout 中全局掛載一次（建議在登入態 layout 的頂層）。
 * 組件會自動判斷是否需要顯示，無需外部傳入 props。
 *
 * @example
 * // 在 AdminLayout 或 AppLayout 中
 * <BulletinAnnouncementPopup />
 */
export default function BulletinAnnouncementPopup() {
  const navigate = useNavigate();

  /** Modal 顯示狀態 */
  const [open, setOpen] = useState(false);

  /** 當前展示的公告列表（已過濾並排序，最多 MAX_DISPLAY 條） */
  const [bulletins, setBulletins] = useState<Bulletin[]>([]);

  /** 樂觀更新已讀狀態：Set<id> 記錄本次 session 中已標記的 id */
  const [readIds, setReadIds] = useState<Set<number>>(new Set());

  /** 單條標記已讀時 loading 的 id */
  const [markingId, setMarkingId] = useState<number | null>(null);

  /** 全部已讀後展示空態 */
  const [allRead, setAllRead] = useState(false);

  /**
   * 組件 mount 時執行一次：
   * 1. 檢查 sessionStorage flag
   * 2. 未顯示過 → 寫入 flag → 請求 API → 過濾排序 → 決定是否開啟 Modal
   */
  useEffect(() => {
    /* 檢查本 session 是否已顯示過彈窗 */
    if (sessionStorage.getItem(SESSION_KEY)) {
      return;
    }

    /* 立即寫入 flag，防止因異步請求導致重複彈出 */
    sessionStorage.setItem(SESSION_KEY, '1');

    /* 異步加載公告數據 */
    void (async () => {
      try {
        const result = await apiClient.get<BulletinListResponse>('/api/bulletin');

        /* 過濾 + 排序 + 截取，委託給可測試的純函式 */
        const displayItems = filterAndSortUnread(result.items);

        if (displayItems.length > 0) {
          setBulletins(displayItems);
          setOpen(true);
        }
        /* 無未讀公告時靜默不彈窗 */
      } catch (err) {
        /* 公告彈窗為增強功能，API 失敗靜默收起，不影響主業務 */
        if (import.meta.env.DEV) console.error('[BulletinPopup] fetch failed', err);
      }
    })();
  }, []);

  /** 關閉彈窗 */
  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  /**
   * 單條標記已讀：先樂觀更新 UI，再異步提交 API。
   * 若 API 失敗，不回滾（靜默失敗，彈窗關閉前的樂觀更新對用戶體驗更友好）。
   */
  const handleMarkRead = useCallback(async (id: number) => {
    /* 樂觀更新 */
    setReadIds((prev) => new Set([...prev, id]));
    setMarkingId(id);

    try {
      await apiClient.post(`/api/bulletin/${id}/read`, {});
    } catch {
      void message.error('標記已讀失敗，請稍後再試');
    } finally {
      setMarkingId(null);
    }
  }, []);

  /**
   * 全部已讀：調用 POST /api/bulletin/read-all。
   * 成功後展示空態 UI（CheckCircle2 + 文字）。
   */
  const handleReadAll = useCallback(async () => {
    try {
      await apiClient.post('/api/bulletin/read-all', {});
      setAllRead(true);
    } catch {
      void message.error('操作失敗，請稍後再試');
    }
  }, []);

  /**
   * 跳轉至公告欄頁面並關閉彈窗
   */
  const handleViewAll = useCallback(() => {
    setOpen(false);
    void navigate({ to: '/bulletin' });
  }, [navigate]);

  /* ── 計算剩餘未讀數量（用於 Tag 顯示） ── */
  const unreadCount = bulletins.filter((b) => !readIds.has(b.id)).length;

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      /* Modal 標題：Megaphone 圖標 + 「系統公告」文字 + 未讀計數 Tag */
      title={
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: space[2],
            fontFamily: font.display,
            fontSize: text.lg,
            fontWeight: weight.semibold,
            color: color.text.primary,
          }}
        >
          <Megaphone size={16} style={{ color: color.brand.primary }} />
          系統公告
          {!allRead && unreadCount > 0 && (
            <Tag
              color="processing"
              style={{
                fontSize: text.xs,
                padding: '0 6px',
                marginLeft: space[1],
              }}
            >
              {unreadCount} 則未讀
            </Tag>
          )}
        </span>
      }
      /* 底部：左側「查看全部公告」鏈接，右側「全部已讀」按鈕 + 「知道了」按鈕 */
      footer={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {/* 左側：查看全部公告鏈接 */}
          <Button
            type="link"
            icon={<Bell size={14} />}
            onClick={handleViewAll}
            style={{
              color: color.text.link,
              padding: 0,
              height: 'auto',
            }}
          >
            查看全部公告
          </Button>

          {/* 右側：操作按鈕組 */}
          <div style={{ display: 'flex', gap: space[2] }}>
            {/* 全部已讀按鈕（有未讀且未進入全部已讀空態時顯示） */}
            {!allRead && unreadCount > 0 && (
              <Button icon={<CheckCircle2 size={14} />} onClick={() => void handleReadAll()}>
                全部已讀
              </Button>
            )}
            {/* 知道了：關閉彈窗 */}
            <Button type="primary" onClick={handleClose}>
              知道了
            </Button>
          </div>
        </div>
      }
      width={560}
      centered
      destroyOnHidden
      styles={{ body: { maxHeight: 'calc(80vh - 120px)', overflowY: 'auto' } }}
    >
      {/* 全部已讀後的空態 UI */}
      {allRead ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: `${space[10]}px 0`,
            gap: space[3],
          }}
        >
          <CheckCircle2 size={40} style={{ color: color.state.success }} strokeWidth={1.5} />
          <p
            style={{
              margin: 0,
              fontSize: text.md,
              color: color.text.secondary,
              fontFamily: font.body,
            }}
          >
            所有公告已標記為已讀
          </p>
        </div>
      ) : (
        /* 公告列表 */
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: space[3],
            paddingBlock: space[2],
          }}
        >
          {bulletins.map((item) => (
            <BulletinCard
              key={item.id}
              item={item}
              markedRead={readIds.has(item.id)}
              onMarkRead={(id) => void handleMarkRead(id)}
              loading={markingId === item.id}
            />
          ))}
        </div>
      )}
    </Modal>
  );
}
